import axios from 'axios';
import classNames from 'classnames';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useUsers } from '../../hooks/useUsers';
import { IAuth0User } from '../../interfaces/IAuth0User';
import Http from '../../services/http';
import { useNotifications } from '../notifications/useNotifications';
import SpinningLoader from '../Loader';
import EditUserModal from './EditUserModal';

const ManageUsers = () => {
  const { data, isLoading, isFetching, isError, refetch } = useUsers();
  const queryClient = useQueryClient();
  const { pushNotification } = useNotifications();
  const [editingUser, setEditingUser] = useState<IAuth0User | null>(null);

  const mutation = useMutation<unknown, unknown, { id: string }>(
    ({ id }) => {
      return axios.delete(`${process.env.REACT_APP_API}/user/${id}`, {
        headers: {
          Authorization: `Bearer ${Http.accessToken}`,
        },
      });
    },
    {
      onSuccess: () => {
        pushNotification({
          heading: 'User Deleted',
          content: `The user was deleted.`,
        });
        queryClient.invalidateQueries('users');
      },
    }
  );

  const updateReferralAccess = useMutation<
    unknown,
    unknown,
    { id: string; canAccessReferrals: boolean }
  >(
    ({ id, canAccessReferrals }) => {
      return axios.patch(
        `${process.env.REACT_APP_API}/user/${id}/referral-access`,
        { can_access_referrals: canAccessReferrals },
        {
          headers: {
            Authorization: `Bearer ${Http.accessToken}`,
          },
        }
      );
    },
    {
      onSuccess: (_, { id, canAccessReferrals }) => {
        queryClient.setQueryData('users', (oldData: any) => {
          if (!oldData) return oldData;

          return oldData.map((user: IAuth0User) => {
            if (user.user_id === id) {
              return {
                ...user,
                user_metadata: {
                  ...user.user_metadata,
                  can_access_referrals: canAccessReferrals,
                },
              };
            }
            return user;
          });
        });

        pushNotification({
          heading: 'Access Updated',
          content: 'Referral access has been updated.',
        });
      },
    }
  );

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <SpinningLoader />
      </div>
    );
  }

  // Only take over the whole panel when we have nothing to show. react-query
  // keeps the last good `data` when a *background* refetch fails, so a flaky
  // refetch after a successful invite/delete must not blank a populated table.
  if (isError && !data) {
    return (
      <div className="-mx-4 mt-8 flex flex-col items-center justify-center gap-3 rounded-lg bg-white py-10 text-center shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0">
        <p className="text-sm text-gray-600">
          We couldn't load the user list. The request may have timed out.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        '-mx-4 mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg',
        {
          'opacity-60': isFetching,
          'animate-pulse': isFetching,
        }
      )}
    >
      {isError && (
        <div className="flex items-center justify-between gap-3 bg-yellow-50 px-4 py-2 text-sm text-yellow-800">
          <span>
            Couldn't refresh the user list, showing the last loaded version.
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            className="font-medium underline whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      )}
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              Name
            </th>
            <th
              scope="col"
              className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            >
              Email
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Client Code
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Referrals Access
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Permissions
            </th>
            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className={'divide-y divide-gray-200 bg-white'}>
          {data
            ?.filter(
              (user: IAuth0User) =>
                !user?.roles?.find((x) => x.name === 'Administrator')
            )
            .sort((a: IAuth0User, b: IAuth0User) =>
              (a.email || '').localeCompare(b.email || '')
            )
            .map((user: IAuth0User) => {
              const clientCodes = Array.isArray(
                user?.user_metadata?.client_code
              )
                ? user?.user_metadata?.client_code.join(', ')
                : user?.user_metadata?.client_code;

              const userType = user?.roles?.find((x) => x.name === 'Broker')
                ? 'Broker'
                : 'Standard';

              return (
                <tr key={user?.email}>
                  <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
                    {user?.nickname || user?.name}
                    <dl className="font-normal lg:hidden">
                      <dt className="sr-only">Title</dt>
                      <dd className="mt-1 truncate text-gray-700">
                        {user?.nickname || user?.name}
                      </dd>
                      <dt className="sr-only sm:hidden">Email</dt>
                      <dd className="mt-1 truncate text-gray-500 sm:hidden">
                        {clientCodes}
                      </dd>
                    </dl>
                  </td>
                  <td className="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                    {user?.email}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {clientCodes}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {userType === 'Standard' && (
                      <input
                        type="checkbox"
                        checked={!!user?.user_metadata?.can_access_referrals}
                        onChange={(e) =>
                          updateReferralAccess.mutate({
                            id: user.user_id,
                            canAccessReferrals: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    )}
                    {userType === 'Broker' && (
                      <span className="text-gray-500">Always</span>
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {userType}
                  </td>
                  <td className="px-3 py-4 text-right text-sm font-medium sm:pr-6">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingUser(user)}
                        className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => mutation.mutate({ id: user?.user_id })}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      {editingUser && (
        <EditUserModal
          user={editingUser}
          open
          onClose={() => setEditingUser(null)}
        />
      )}
    </div>
  );
};

export default ManageUsers;
